<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, Ref } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { UserLanguageCode, Theme } from '@/types'
import * as langService from '@/services/lang'
import { loadLanguageAsync } from '@/setup/setup-i18n'
import * as themeService from '@/services/theme'
import { setPageMeta } from '@/router/utils'
import { nrAbandonLoginProcess } from '@/services/newrelic'
import { NR_STEPS } from '@/services/newrelic/constants'
import { clearSessionTracker, clearIncompleteStep, getCurrentStep, getSessionId as saveSessionId } from '@/services/auth/authx-session'
import { saveSessionStart } from '@/services/auth'
import { useRoute, router } from './router'

const { useState: useLoginDomainStoreState, useActions: useLoginDomainStoreActions } = createNamespacedHelpers('LoginDomain')

export default defineComponent({
  name: 'App',

  setup() {

    saveSessionId()

    const theme = useLoginDomainStoreState(['theme']).theme as Ref<Theme>
    const { getLoginDomain } = useLoginDomainStoreActions(['getLoginDomain'])

    const loading = ref(true)
    const language: Ref<UserLanguageCode> = ref(null)

    const currentRoute = useRoute()

    const getUrlLanguage = (): UserLanguageCode => {
      const code = currentRoute.value?.query.langCode as string

      if (UserLanguageCode[code]) {
        return code as UserLanguageCode
      }

      return null

    }

    const setLanguage = () => {

      if (getUrlLanguage()) {
        language.value = getUrlLanguage()
        return
      }

      try {
        const preferredLanguages = langService.getBrowserLanguage()
        language.value = langService.pickSuitableLanguage(preferredLanguages)
      } catch {
        language.value = UserLanguageCode.en
      }

    }

    const handleLanguage = async () => {
      try {
        setLanguage()
        await loadLanguageAsync(language.value)
      } catch {
        language.value = UserLanguageCode.en
      }
    }

    const handleLoginDomain = async () => {

      try {

        await getLoginDomain(window.location.hostname)

        // Apply the non-reactive them changes, such as CSS variables
        themeService.applyTheme(theme.value)

        // Update the page meta title to use the `theme.name` content
        setPageMeta(currentRoute.value)

      } catch (error) {
        router.replace({ name: 'UnknownErrorPage' })
      } finally {
        loading.value = false
      }

    }

    const handleBeforeUnload = () => {
      const step = getCurrentStep()
      if (step && step !== NR_STEPS.LOGIN_EMAIL) {
        nrAbandonLoginProcess()
        clearIncompleteStep()
        clearSessionTracker()
      }
    }

    onMounted(async () => {
      // Load the localised content
      await handleLanguage()
      // Load the login domain for this domain or for the passed themeCode
      await handleLoginDomain()

      window.addEventListener('beforeunload', handleBeforeUnload)

    })

    onUnmounted(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    })

    return {
      loading,
    }
  },
})
</script>

<template>
  <div
    id="app"
  >
    <div v-if="loading" class="app-loading">
      <sm-loader />
    </div>
    <router-view v-if="!loading" />
  </div>
</template>

<style lang="scss">
  @import 'assets/styles/variables.scss';
  @import 'assets/styles/base.scss';

  .app-loading {
    @apply flex justify-center items-center min-h-screen;
  }
</style>
